import clsx from 'clsx';
import { useReducedMotion } from 'framer-motion';
import { type ComponentType, forwardRef, memo, useMemo } from 'react';
import { Streamdown, type AnimateOptions } from 'streamdown';

import { useAriaLabelingProps } from '../../core/hooks/useAriaLabelingProps.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useSafeForwardProps } from '../../core/hooks/useSafeForwardProps.js';
import type { AriaLabelingProps } from '../../core/types/a11y-props.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import type { DataTestId } from '../../core/types/data-props.js';
import type { DOMProps } from '../../core/types/dom.js';
import type { MaskingProps } from '../../core/types/masking-props.js';
import type { StylingProps } from '../../core/types/styling-props.js';
// Registers the global [data-sd-animate] styles needed by Streamdown's animated prop.
import type { AiResponseCodeBlockProps } from './ai-response-types.js';
import * as styles from './AiResponse.sty.js';
import { AiResponseBlock } from './components/AiResponseBlock.js';
import {
  ANIMATION_DURATION_MS,
  useTextAnimation,
} from './hooks/useTextAnimation.js';
import { getStreamdownComponents } from './utils/component-mappings.js';

const COMPONENT_NAME = 'AiResponse';

const animationOptions: AnimateOptions = {
  animation: 'aiResponseAnimation',
  duration: ANIMATION_DURATION_MS,
  easing: 'cubic-bezier(0, 0, 0.2, 1)',
  sep: 'word',
};

/**
 * Accepted properties for `AiResponse`.
 * @public
 */
export interface AiResponseProps
  extends
    DataTestId,
    StylingProps,
    MaskingProps,
    DOMProps,
    AriaLabelingProps,
    BehaviorTrackingProps {
  /**
   * The content to be displayed as markdown.
   */
  children: string;

  /**
   * A map of fenced code block languages to components that render the content of such a
   * block instead of the default code snippet. A fenced block opened with the language
   * `dt-visualization` is handed to the renderer registered under `'dt-visualization'`;
   * blocks with any other language and inline code keep their default rendering.
   * Provide a stable reference, for example by wrapping the map in `useMemo`.
   * @defaultValue undefined
   */
  customCodeBlockRenderers?: Record<
    string,
    ComponentType<AiResponseCodeBlockProps>
  >;

  /**
   * Callback that is called whenever the animation state changes.
   */
  onAnimationStateChange?: (isAnimating: boolean) => void;

  /**
   * Controls how the content is rendered and animated.
   * - `'static'`: Renders the entire response immediately with no animation.
   * - `'streaming'`: Animates content as it streams in.
   * - `'complete'`: Indicates streaming has finished and the full content has been passed. The component will finish animating any remaining content and turn off animation.
   *
   * @defaultValue 'streaming'
   */
  responseState?: 'static' | 'streaming' | 'complete';
}

const InternalAiResponse = forwardRef<HTMLDivElement, AiResponseProps>(
  (props, forwardedRef) => {
    const {
      children,
      customCodeBlockRenderers,
      onAnimationStateChange,
      responseState = 'streaming',
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
      ...remainingProps
    } = props;

    const [behavioralTrackingProps, restProps] =
      useBehavioralTrackingProps(remainingProps);
    const ariaLabelingProps = useAriaLabelingProps(restProps);
    const forwardProps = useSafeForwardProps(restProps, ariaLabelingProps);

    const components = useMemo(
      () => getStreamdownComponents(customCodeBlockRenderers),
      [customCodeBlockRenderers],
    );

    const shouldReduceMotion = useReducedMotion() ?? false;
    const { displayedText, isTextAnimating } = useTextAnimation(
      children,
      shouldReduceMotion,
      responseState,
      onAnimationStateChange,
    );

    if (displayedText.length === 0) {
      return null;
    }

    return (
      <div
        ref={forwardedRef}
        aria-live="polite"
        aria-atomic="true"
        style={consumerStyle}
        className={clsx(styles.wrapper, consumerClassName)}
        data-testid={dataTestId}
        data-dtrum-mask={dataDtrumMask}
        data-dtrum-allow={dataDtrumAllow}
        {...ariaLabelingProps}
        {...behavioralTrackingProps}
        {...forwardProps}
        data-dt-component={COMPONENT_NAME}
      >
        <Streamdown
          animated={shouldReduceMotion ? false : animationOptions}
          isAnimating={responseState === 'streaming' || isTextAnimating}
          components={components}
          BlockComponent={AiResponseBlock}
          className={styles.streamdown}
        >
          {displayedText}
        </Streamdown>
      </div>
    );
  },
);

(
  InternalAiResponse as typeof InternalAiResponse & { displayName: string }
).displayName = COMPONENT_NAME;

/**
 * Use the `AiResponse` component to animate AI-generated content.
 * @public
 */
export const AiResponse = memo(InternalAiResponse);
